import { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import { Link } from "react-router-dom";
import { Col } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../services/api";

// Cache para armazenar resultados das chamadas API (ENEM)
const notasCache = new Map();

// Função de debounce
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Campos da correção UERJ
const CAMPOS_UERJ = [
  "adequacao_tema",
  "tipo_texto",
  "argumentacao",
  "periodo_coesao",
  "modalidade",
];

const trazerTotalUerj = (notaUerj) => {
  if (!notaUerj) return 0;

  // 1) se o back já mandou nota_final/nota_total, usa ela
  if (notaUerj.nota_final != null) {
    return Number(notaUerj.nota_final) || 0;
  }
  if (notaUerj.nota_total != null) {
    return Number(notaUerj.nota_total) || 0;
  }

  // 2) fallback: soma os campos (0–20) e arredonda igual à tela de correção
  const soma = CAMPOS_UERJ.reduce((total, campo) => {
    const valor = Number(notaUerj[campo]) || 0;
    return total + valor;
  }, 0);

  return Math.round(soma);
};

const MostrarNota = memo(
  ({ alunoId, atividadeId, turmaId, tipoTurma, notaPreCarregada }) => {
    const [loading, setLoading] = useState(true);
    const [notas, setNotas] = useState([]);
    const abortControllerRef = useRef(null);

    // chave para cache (só usada pros outros tipos de turma)
    const cacheKey = useMemo(
      () => `${alunoId}-${atividadeId}-${turmaId}`,
      [alunoId, atividadeId, turmaId]
    );

    // ENEM: soma das 5 competências
    const trazerTotal = (nota) => {
      const total =
        parseInt(nota.competencia1) +
        parseInt(nota.competencia2) +
        parseInt(nota.competencia3) +
        parseInt(nota.competencia4) +
        parseInt(nota.competencia5);

      if (nota.falta) {
        return 0;
      }

      return total;
    };

    const loadNotas = useCallback(async () => {
      // UERJ não usa /pegarNotas – saímos cedo
      if (tipoTurma === "UERJ") return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (notasCache.has(cacheKey)) {
        const cachedData = notasCache.get(cacheKey);
        setNotas(cachedData);
        setLoading(false);
        return;
      }

      abortControllerRef.current = new AbortController();

      try {
        const response = await api.get(
          `/pegarNotas/${alunoId}/${atividadeId}/${turmaId}`,
          { signal: abortControllerRef.current.signal }
        );
        notasCache.set(cacheKey, response.data);
        setNotas(response.data);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Erro ao carregar notas:", err);
          setLoading(false);
        }
      }
    }, [alunoId, atividadeId, turmaId, cacheKey, tipoTurma]);

    const debouncedLoadNotas = useMemo(
      () => debounce(loadNotas, 300),
      [loadNotas]
    );

    const habilitarReenvio = useCallback(
      async (id) => {
        try {
          await api.put(`/reenviarRedacao/${id}`);
          notasCache.delete(cacheKey);
          setLoading(true);
          toast.success("Reenvio de redação habilitado!");
          loadNotas();
        } catch (err) {
          toast.error("Não foi possível habilitar o Reenvio da redação!");
        }
      },
      [cacheKey, loadNotas]
    );

    useEffect(() => {
      // UERJ: não faz request, só desliga o loading
      if (tipoTurma === "UERJ") {
        setLoading(false);
        return;
      }

      if (alunoId && atividadeId && turmaId) {
        setLoading(true);
        debouncedLoadNotas();
      }

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [alunoId, atividadeId, turmaId, debouncedLoadNotas, tipoTurma]);

    // --- RENDER ---

    // UERJ com nota já vinda da rota /alunos/:turma_id
    if (tipoTurma === "UERJ" && notaPreCarregada) {
      const total = trazerTotalUerj(notaPreCarregada);

      return (
        <td>
          <strong className="badge badge-pill badge-success">
            Nota: {total}/20
          </strong>
        </td>
      );
    }

    // Demais turmas (ENEM / IA / etc)
    return (
      <>
        {loading ? (
          <td>
            <Col className="text-center">
              <div className="shadow p-3 mb-4 bg-white rounded fdark flex-c">
                <div>
                  <div className="spinner-grow text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <div className="spinner-grow text-secondary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
            </Col>
          </td>
        ) : (
          <>
            {notas.length > 0 ? (
              notas.map((nota) => (
                <td key={nota.id}>
                  {nota.corrigida && nota.corrigida === 1 ? (
                    <Link to={`/corrigir/redacao/${nota.id}`}>
                      <strong className="badge badge-pill badge-success">
                        Nota: {trazerTotal(nota)}
                      </strong>
                    </Link>
                  ) : (
                    <>
                      {nota.finalizada && nota.finalizada === 1 ? (
                        <>
                          <strong className="badge badge-pill badge-info">
                            Aguardando Correção
                          </strong>
                          <strong
                            className="badge badge-pill badge-primary mhover"
                            onClick={() => habilitarReenvio(nota.id)}
                          >
                            Habilitar Reenvio
                          </strong>
                        </>
                      ) : (
                        <strong className="badge badge-pill badge-danger">
                          Pendente
                        </strong>
                      )}
                    </>
                  )}
                </td>
              ))
            ) : (
              <td>
                <strong className="badge badge-pill badge-warning">
                  inexistente
                </strong>
              </td>
            )}
          </>
        )}
      </>
    );
  }
);

export default MostrarNota;

// Função para limpar o cache quando necessário
export const clearNotasCache = () => {
  notasCache.clear();
};